import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import useClickOutside from './useClickOutside'
import { startProCheckout } from '../../proCheckout'
import api from '../../api'
import { downloadKML, downloadCSV, geoJSONBounds } from '../../utils'
import { selectionTypes } from '../../constants'
import ShareImageModal from './ShareImageModal'
import ShareByEmailModal from './ShareByEmailModal'
import Wkt from 'wicket/wicket'
import { HugeiconsIcon } from '@hugeicons/react'
import { CopyIcon as CopyIconFromHuge, Mail01Icon, Download01Icon, WhatsappIcon as WhatsappIconFromHuge, FileExportIcon, Share01Icon } from '@hugeicons/core-free-icons'

const getNumericValue = (val) => {
  if (val == null) return 0
  if (typeof val === 'number') return isNaN(val) ? 0 : val
  const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ''))
  return isNaN(parsed) ? 0 : parsed
}

const getEntryPopulation = (entry) => {
  if (entry == null) return 0
  if (typeof entry === 'number') return isNaN(entry) ? 0 : entry
  if (typeof entry === 'string') return getNumericValue(entry)
  if (entry.sum != null && getNumericValue(entry.sum) > 0) return getNumericValue(entry.sum)
  if (entry.sum_pop != null && getNumericValue(entry.sum_pop) > 0) return getNumericValue(entry.sum_pop)
  if (entry.pop_tot != null && getNumericValue(entry.pop_tot) > 0) return getNumericValue(entry.pop_tot)
  if (entry.total_population != null && getNumericValue(entry.total_population) > 0) return getNumericValue(entry.total_population)
  if (entry.population != null && getNumericValue(entry.population) > 0) return getNumericValue(entry.population)
  if (entry.value != null && getNumericValue(entry.value) > 0) return getNumericValue(entry.value)
  return 0
}

const isWorldpopEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return false
  const ds = (entry.type || entry.ds_name || entry.dataset || entry.source || '').toString().toLowerCase()
  return ds.includes('worldpop') || ds.includes('residential')
}

const isLandscanEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return false
  const ds = (entry.type || entry.ds_name || entry.dataset || entry.source || '').toString().toLowerCase()
  return ds.includes('landscan') || ds.includes('daytime') || (!ds && !isWorldpopEntry(entry))
}

const resolveShapeName = (item, rightSidebarStore, selectionStore) => {
  if (selectionStore && selectionStore.selectedMenuItems && selectionStore.selectedMenuItems.length > 1) {
    return `${selectionStore.selectedMenuItems.length} Selected Areas`
  }
  const target = (selectionStore && selectionStore.editableShape) ||
    (selectionStore && selectionStore.item) ||
    item ||
    (rightSidebarStore && rightSidebarStore.singlePolygonData)

  if (target) {
    if (target.name && typeof target.name === 'string' && target.name.trim() && target.name !== 'Selected Area' && target.name !== 'Selected Location') return target.name.trim()
    if (target.title && typeof target.title === 'string' && target.title.trim() && target.title !== 'Selected Area' && target.title !== 'Selected Location') return target.title.trim()
    if (target.shape_name && typeof target.shape_name === 'string' && target.shape_name.trim()) return target.shape_name.trim()
    if (target.displayName && typeof target.displayName === 'string' && target.displayName.trim()) return target.displayName.trim()
    if (target.zip_code) return String(target.zip_code).trim()
    if (target.zipcode) return String(target.zipcode).trim()
    if (target.zip) return String(target.zip).trim()
    if (target.properties) {
      const p = target.properties
      if (p.name && typeof p.name === 'string' && p.name.trim()) return p.name.trim()
      if (p.title && typeof p.title === 'string' && p.title.trim()) return p.title.trim()
      if (p.NAME && typeof p.NAME === 'string' && p.NAME.trim()) return p.NAME.trim()
      if (p.zip_code) return String(p.zip_code).trim()
      if (p.zipcode) return String(p.zipcode).trim()
      if (p.ZIP) return String(p.ZIP).trim()
      if (p.ZIPCODE) return String(p.ZIPCODE).trim()
    }
  }

  if (rightSidebarStore && rightSidebarStore.targetGroupName && rightSidebarStore.targetGroupName.trim()) {
    return rightSidebarStore.targetGroupName.trim()
  }

  return (target && (target.name || target.title)) || 'Selected Territory'
}

const extractLandscan = (obj, targetYear) => {
  if (!obj) return 0

  // 1. Direct stats.total_population array
  if (obj.stats && Array.isArray(obj.stats.total_population) && obj.stats.total_population.length > 0) {
    const popArr = obj.stats.total_population
    const lsItems = popArr.filter(isLandscanEntry)
    const listToUse = lsItems.length > 0 ? lsItems : popArr
    if (targetYear) {
      const match = listToUse.find(p => String(p.year) === String(targetYear))
      const val = getEntryPopulation(match)
      if (val > 0) return val
    }
    for (const y of ['2024', '2026', '2021', '2022', '2023', '2025']) {
      const match = listToUse.find(p => String(p.year) === y)
      const val = getEntryPopulation(match)
      if (val > 0) return val
    }
    for (let i = listToUse.length - 1; i >= 0; i--) {
      const val = getEntryPopulation(listToUse[i])
      if (val > 0) return val
    }
  }

  // 2. Direct stats properties
  if (obj.stats) {
    if (obj.stats.population != null && getNumericValue(obj.stats.population) > 0) return getNumericValue(obj.stats.population)
    if (obj.stats.total_population != null && getNumericValue(obj.stats.total_population) > 0) return getNumericValue(obj.stats.total_population)
    if (obj.stats.daytime_population != null && getNumericValue(obj.stats.daytime_population) > 0) return getNumericValue(obj.stats.daytime_population)
    if (obj.stats.sum_pop != null && getNumericValue(obj.stats.sum_pop) > 0) return getNumericValue(obj.stats.sum_pop)
    if (obj.stats.sum != null && getNumericValue(obj.stats.sum) > 0) return getNumericValue(obj.stats.sum)
  }

  // 3. population_datas (object or array)
  if (obj.population_datas) {
    const pDatas = obj.population_datas
    if (typeof pDatas === 'object' && !Array.isArray(pDatas)) {
      const yearKeys = Object.keys(pDatas).sort()
      const keysToTry = [
        ...(targetYear ? [String(targetYear)] : []),
        '2024', '2026', '2021', '2022', '2023', '2025',
        ...yearKeys.slice().reverse()
      ]
      const tried = new Set()
      for (const k of keysToTry) {
        if (!k || tried.has(k) || !pDatas[k]) continue
        tried.add(k)
        const bucket = pDatas[k]
        if (Array.isArray(bucket)) {
          const ls = bucket.find(isLandscanEntry) || bucket[0]
          const val = getEntryPopulation(ls)
          if (val > 0) return val
          for (const item of bucket) {
            const v = getEntryPopulation(item)
            if (v > 0) return v
          }
        } else {
          const val = getEntryPopulation(bucket)
          if (val > 0) return val
        }
      }
    } else if (Array.isArray(pDatas) && pDatas.length > 0) {
      const lsItems = pDatas.filter(isLandscanEntry)
      const listToUse = lsItems.length > 0 ? lsItems : pDatas
      for (const item of listToUse) {
        const val = getEntryPopulation(item)
        if (val > 0) return val
      }
    }
  }

  // 4. trends array
  if (Array.isArray(obj.trends) && obj.trends.length > 0) {
    if (targetYear) {
      const match = obj.trends.find(t => String(t.year) === String(targetYear))
      const val = getEntryPopulation(match)
      if (val > 0) return val
    }
    for (const y of ['2024', '2026', '2021', '2022', '2023', '2025']) {
      const match = obj.trends.find(t => String(t.year) === y)
      const val = getEntryPopulation(match)
      if (val > 0) return val
    }
    for (let i = obj.trends.length - 1; i >= 0; i--) {
      const val = getEntryPopulation(obj.trends[i])
      if (val > 0) return val
    }
  }

  // 5. analysis (RegionAnalysis)
  if (obj.analysis) {
    if (obj.analysis.sum != null && getNumericValue(obj.analysis.sum) > 0) return getNumericValue(obj.analysis.sum)
    if (obj.analysis.sum_pop != null && getNumericValue(obj.analysis.sum_pop) > 0) return getNumericValue(obj.analysis.sum_pop)
    if (obj.analysis.population != null && getNumericValue(obj.analysis.population) > 0) return getNumericValue(obj.analysis.population)
    if (obj.analysis.total_population != null && getNumericValue(obj.analysis.total_population) > 0) return getNumericValue(obj.analysis.total_population)
  }

  // 6. Direct properties on obj
  if (obj.population != null && getNumericValue(obj.population) > 0) return getNumericValue(obj.population)
  if (obj.total_population != null && getNumericValue(obj.total_population) > 0) return getNumericValue(obj.total_population)
  if (obj.sum != null && getNumericValue(obj.sum) > 0) return getNumericValue(obj.sum)
  if (obj.sum_pop != null && getNumericValue(obj.sum_pop) > 0) return getNumericValue(obj.sum_pop)

  // 7. GeoJSON / properties
  if (obj.properties) {
    const p = obj.properties
    if (p.population != null && getNumericValue(p.population) > 0) return getNumericValue(p.population)
    if (p.total_population != null && getNumericValue(p.total_population) > 0) return getNumericValue(p.total_population)
    if (p.sum != null && getNumericValue(p.sum) > 0) return getNumericValue(p.sum)
    if (p.sum_pop != null && getNumericValue(p.sum_pop) > 0) return getNumericValue(p.sum_pop)
    if (p.pop != null && getNumericValue(p.pop) > 0) return getNumericValue(p.pop)
    if (p.DAYTIME_POP != null && getNumericValue(p.DAYTIME_POP) > 0) return getNumericValue(p.DAYTIME_POP)
  }

  // 8. Child collections (geopolygons, boundaries, etc.)
  if (Array.isArray(obj.geopolygons) && obj.geopolygons.length > 0) {
    let sum = 0
    obj.geopolygons.forEach(g => { sum += extractLandscan(g, targetYear) })
    if (sum > 0) return sum
  }
  if (Array.isArray(obj.boundaries) && obj.boundaries.length > 0) {
    let sum = 0
    obj.boundaries.forEach(b => { sum += extractLandscan(b, targetYear) })
    if (sum > 0) return sum
  }
  if (Array.isArray(obj.zipCodeBoundaries) && obj.zipCodeBoundaries.length > 0) {
    let sum = 0
    obj.zipCodeBoundaries.forEach(z => { sum += extractLandscan(z, targetYear) })
    if (sum > 0) return sum
  }
  if (Array.isArray(obj.shapes) && obj.shapes.length > 0) {
    let sum = 0
    obj.shapes.forEach(s => { sum += extractLandscan(s, targetYear) })
    if (sum > 0) return sum
  }

  return 0
}

const resolveDaytimePopulation = (item, rightSidebarStore, selectionStore) => {
  const targetYear = '2024'

  // 1. Single polygon data
  if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
    const val = extractLandscan(rightSidebarStore.singlePolygonData, targetYear)
    if (val > 0) return val
  }
  if (rightSidebarStore && Array.isArray(rightSidebarStore.rawSinglePolygonData) && rightSidebarStore.rawSinglePolygonData.length > 0) {
    const val = extractLandscan(rightSidebarStore.rawSinglePolygonData[0], targetYear)
    if (val > 0) return val
  }

  // 2. Item
  if (item) {
    const val = extractLandscan(item, targetYear)
    if (val > 0) return val
  }

  // 3. SelectionStore item
  if (selectionStore && selectionStore.item) {
    const val = extractLandscan(selectionStore.item, targetYear)
    if (val > 0) return val
  }

  // 4. Multi-selection items
  if (selectionStore && Array.isArray(selectionStore.selectedMenuItems) && selectionStore.selectedMenuItems.length > 0) {
    let sum = 0
    selectionStore.selectedMenuItems.forEach(m => { sum += extractLandscan(m, targetYear) })
    if (sum > 0) return sum
  }

  // 5. PolygonData
  const polyList = (rightSidebarStore && Array.isArray(rightSidebarStore.rawPolygonData) && rightSidebarStore.rawPolygonData.length > 0)
    ? rightSidebarStore.rawPolygonData
    : (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData) ? rightSidebarStore.polygonData : [])

  if (polyList.length > 0) {
    let sum = 0
    polyList.forEach(p => {
      sum += extractLandscan(p, targetYear)
    })
    if (sum > 0) return sum
  }

  // 6. RightSidebarStore populationData.data & dissolved_data for 2024
  if (rightSidebarStore && rightSidebarStore.populationData) {
    const popData = rightSidebarStore.populationData
    if (Array.isArray(popData.data) && popData.data.length > 0) {
      const match = popData.data.find(d => String(d.year) === '2024' && isLandscanEntry(d)) ||
        popData.data.find(d => String(d.year) === '2024') ||
        popData.data.find(d => isLandscanEntry(d)) ||
        popData.data[popData.data.length - 1]
      if (match) {
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
    }
    if (Array.isArray(popData.dissolved_data) && popData.dissolved_data.length > 0) {
      const match = popData.dissolved_data.find(d => String(d.year) === '2024') ||
        popData.dissolved_data[popData.dissolved_data.length - 1]
      if (match) {
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
    }
  }

  // 7. populationTrends for 2024
  if (rightSidebarStore && Array.isArray(rightSidebarStore.populationTrends) && rightSidebarStore.populationTrends.length > 0) {
    const match = rightSidebarStore.populationTrends.find(t => String(t.year) === '2024') ||
      rightSidebarStore.populationTrends[rightSidebarStore.populationTrends.length - 1]
    if (match) {
      const val = getEntryPopulation(match)
      if (val > 0) return val
    }
  }

  // 8. RightSidebarStore totalPopulation if 2024 Landscan is selected
  if (rightSidebarStore && rightSidebarStore.populationDataset !== 'worldpop' && String(rightSidebarStore.populationSelectedYear) === '2024' && rightSidebarStore.totalPopulation != null && getNumericValue(rightSidebarStore.totalPopulation) > 0) {
    return getNumericValue(rightSidebarStore.totalPopulation)
  }

  // 9. incomeAndPopDataForChart
  if (rightSidebarStore && Array.isArray(rightSidebarStore.incomeAndPopDataForChart) && rightSidebarStore.incomeAndPopDataForChart.length > 0) {
    let sum = 0
    rightSidebarStore.incomeAndPopDataForChart.forEach(d => {
      if (d && getNumericValue(d.population) > 0) sum += getNumericValue(d.population)
    })
    if (sum > 0) return sum
  }

  // 10. Fallback: RightSidebarStore totalPopulation
  if (rightSidebarStore && rightSidebarStore.totalPopulation != null && getNumericValue(rightSidebarStore.totalPopulation) > 0) {
    return getNumericValue(rightSidebarStore.totalPopulation)
  }

  return 0
}

const extractWorldpop2026 = (obj) => {
  if (!obj) return 0

  // 1. Check population_datas for 2026
  if (obj.population_datas && typeof obj.population_datas === 'object') {
    const pDatas = obj.population_datas
    for (const key of ['2026', 2026]) {
      if (pDatas[key] !== undefined) {
        const bucket = pDatas[key]
        if (Array.isArray(bucket)) {
          const wp = bucket.find(isWorldpopEntry) || bucket[0]
          const val = getEntryPopulation(wp)
          if (val > 0) return val
        } else {
          const val = getEntryPopulation(bucket)
          if (val > 0) return val
        }
      }
    }
    // Check all years in population_datas for worldpop if 2026 wasn't matched
    for (const yearKey of Object.keys(pDatas).sort()) {
      const bucket = pDatas[yearKey]
      if (Array.isArray(bucket)) {
        const wp = bucket.find(isWorldpopEntry)
        if (wp) {
          const val = getEntryPopulation(wp)
          if (val > 0) return val
        }
      }
    }
  }

  // 2. Check stats.total_population
  if (obj.stats && Array.isArray(obj.stats.total_population)) {
    const totalPop = obj.stats.total_population
    // Look for 2026 worldpop specifically
    const wp2026 = totalPop.find(p => p && String(p.year) === '2026' && isWorldpopEntry(p))
    if (wp2026) {
      const val = getEntryPopulation(wp2026)
      if (val > 0) return val
    }
    // Look for any 2026 entry
    const any2026 = totalPop.find(p => p && String(p.year) === '2026')
    if (any2026) {
      const val = getEntryPopulation(any2026)
      if (val > 0) return val
    }
    // Look for any worldpop entry
    const anyWp = totalPop.filter(isWorldpopEntry)
    if (anyWp.length > 0) {
      const val = getEntryPopulation(anyWp[anyWp.length - 1])
      if (val > 0) return val
    }
  }

  // 3. Check trends
  if (Array.isArray(obj.trends)) {
    const t2026 = obj.trends.find(t => t && String(t.year) === '2026')
    if (t2026) {
      const val = getEntryPopulation(t2026)
      if (val > 0) return val
    }
  }

  // 4. Nested geopolygons/boundaries (e.g. TargetGroup object)
  if (Array.isArray(obj.geopolygons) && obj.geopolygons.length > 0) {
    let sum = 0
    obj.geopolygons.forEach(g => { sum += extractWorldpop2026(g) })
    if (sum > 0) return sum
  }
  if (Array.isArray(obj.boundaries) && obj.boundaries.length > 0) {
    let sum = 0
    obj.boundaries.forEach(b => { sum += extractWorldpop2026(b) })
    if (sum > 0) return sum
  }

  return 0
}

const resolveResidentialPopulation = (item, rightSidebarStore, selectionStore) => {
  // 1. Single polygon from rightSidebarStore
  if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
    const val = extractWorldpop2026(rightSidebarStore.singlePolygonData)
    if (val > 0) return val
  }
  if (rightSidebarStore && Array.isArray(rightSidebarStore.rawSinglePolygonData) && rightSidebarStore.rawSinglePolygonData.length > 0) {
    const val = extractWorldpop2026(rightSidebarStore.rawSinglePolygonData[0])
    if (val > 0) return val
  }

  // 2. Item passed into dropdown
  if (item) {
    const val = extractWorldpop2026(item)
    if (val > 0) return val
  }

  // 3. SelectionStore item
  if (selectionStore && selectionStore.item) {
    const val = extractWorldpop2026(selectionStore.item)
    if (val > 0) return val
  }

  // 4. Selected line population datas
  if (rightSidebarStore && rightSidebarStore.selectedLinePopulationDatas) {
    const val = extractWorldpop2026({ population_datas: rightSidebarStore.selectedLinePopulationDatas })
    if (val > 0) return val
  }

  // 5. If Target Group / multiple polygons in rightSidebarStore
  const polyList = (rightSidebarStore && Array.isArray(rightSidebarStore.rawPolygonData) && rightSidebarStore.rawPolygonData.length > 0)
    ? rightSidebarStore.rawPolygonData
    : (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData) ? rightSidebarStore.polygonData : [])

  if (polyList.length > 0) {
    let sum = 0
    polyList.forEach(p => {
      sum += extractWorldpop2026(p)
    })
    if (sum > 0) return sum
  }

  // 6. RightSidebarStore populationData.data
  if (rightSidebarStore && rightSidebarStore.populationData && Array.isArray(rightSidebarStore.populationData.data)) {
    const wp2026 = rightSidebarStore.populationData.data.find(d => d && String(d.year) === '2026' && isWorldpopEntry(d))
    if (wp2026) {
      const val = getEntryPopulation(wp2026)
      if (val > 0) return val
    }
    const any2026 = rightSidebarStore.populationData.data.find(d => d && String(d.year) === '2026')
    if (any2026) {
      const val = getEntryPopulation(any2026)
      if (val > 0) return val
    }
  }

  // 7. RightSidebarStore populationTrends
  if (rightSidebarStore && Array.isArray(rightSidebarStore.populationTrends)) {
    const t2026 = rightSidebarStore.populationTrends.find(t => t && String(t.year) === '2026')
    if (t2026) {
      const val = getEntryPopulation(t2026)
      if (val > 0) return val
    }
  }

  // 8. If worldpop dataset is currently active in rightSidebarStore
  if (rightSidebarStore && rightSidebarStore.populationDataset === 'worldpop' && rightSidebarStore.totalPopulation != null && rightSidebarStore.totalPopulation > 0) {
    return rightSidebarStore.totalPopulation
  }

  return null
}

const extractIncome = (obj) => {
  if (!obj) return 0

  // 1. stats.hh_income (array)
  if (obj.stats && Array.isArray(obj.stats.hh_income) && obj.stats.hh_income.length > 0) {
    const valid = obj.stats.hh_income.filter(x => x && (getNumericValue(x.value) > 0 || getNumericValue(x.income) > 0 || getNumericValue(x.hh_income) > 0))
    if (valid.length > 0) {
      const y2021 = valid.find(x => String(x.year) === '2021' || x.year === 2021)
      if (y2021) return getNumericValue(y2021.value) || getNumericValue(y2021.income) || getNumericValue(y2021.hh_income)
      const y2024 = valid.find(x => String(x.year) === '2024' || x.year === 2024)
      if (y2024) return getNumericValue(y2024.value) || getNumericValue(y2024.income) || getNumericValue(y2024.hh_income)
      const last = valid[valid.length - 1]
      return getNumericValue(last.value) || getNumericValue(last.income) || getNumericValue(last.hh_income)
    }
  }

  // 2. stats direct income properties
  if (obj.stats) {
    if (obj.stats.median_hh_income != null && getNumericValue(obj.stats.median_hh_income) > 0) return getNumericValue(obj.stats.median_hh_income)
    if (obj.stats.median_income != null && getNumericValue(obj.stats.median_income) > 0) return getNumericValue(obj.stats.median_income)
    if (obj.stats.hh_income != null && getNumericValue(obj.stats.hh_income) > 0) return getNumericValue(obj.stats.hh_income)
    if (obj.stats.income != null && getNumericValue(obj.stats.income) > 0) return getNumericValue(obj.stats.income)
    if (obj.stats.average_income != null && getNumericValue(obj.stats.average_income) > 0) return getNumericValue(obj.stats.average_income)
  }

  // 3. incomeData array
  if (Array.isArray(obj.incomeData) && obj.incomeData.length > 0) {
    const valid = obj.incomeData.filter(x => x && (getNumericValue(x.income) > 0 || getNumericValue(x.hh_income) > 0 || getNumericValue(x.value) > 0))
    if (valid.length > 0) {
      const y2021 = valid.find(x => String(x.year) === '2021' || x.year === 2021)
      if (y2021) return getNumericValue(y2021.income) || getNumericValue(y2021.hh_income) || getNumericValue(y2021.value)
      const last = valid[valid.length - 1]
      return getNumericValue(last.income) || getNumericValue(last.hh_income) || getNumericValue(last.value)
    }
  }

  // 4. population_datas
  if (obj.population_datas) {
    const pDatas = obj.population_datas
    if (typeof pDatas === 'object' && !Array.isArray(pDatas)) {
      const yearKeys = Object.keys(pDatas).sort()
      const keysToTry = ['2021', '2024', 2021, 2024, ...yearKeys.slice().reverse()]
      const tried = new Set()
      for (const y of keysToTry) {
        if (!y || tried.has(String(y)) || !pDatas[String(y)]) continue
        tried.add(String(y))
        const bucket = pDatas[String(y)]
        const arr = Array.isArray(bucket) ? bucket : [bucket]
        for (const d of arr) {
          if (!d) continue
          const v = getNumericValue(d.hh_income) || getNumericValue(d.income) || getNumericValue(d.total_income) || getNumericValue(d.average_income) || getNumericValue(d.median_income)
          if (v > 0) return v
        }
      }
    } else if (Array.isArray(pDatas) && pDatas.length > 0) {
      for (const d of pDatas) {
        if (!d) continue
        const v = getNumericValue(d.hh_income) || getNumericValue(d.income) || getNumericValue(d.total_income) || getNumericValue(d.average_income)
        if (v > 0) return v
      }
    }
  }

  // 5. analysis (RegionAnalysis)
  if (obj.analysis) {
    if (obj.analysis.hh_income != null && getNumericValue(obj.analysis.hh_income) > 0) return getNumericValue(obj.analysis.hh_income)
    if (obj.analysis.median_income != null && getNumericValue(obj.analysis.median_income) > 0) return getNumericValue(obj.analysis.median_income)
    if (obj.analysis.income != null && getNumericValue(obj.analysis.income) > 0) return getNumericValue(obj.analysis.income)
    if (obj.analysis.average_income != null && getNumericValue(obj.analysis.average_income) > 0) return getNumericValue(obj.analysis.average_income)
  }

  // 6. Direct properties
  if (obj.median_hh_income != null && getNumericValue(obj.median_hh_income) > 0) return getNumericValue(obj.median_hh_income)
  if (obj.hh_income != null && getNumericValue(obj.hh_income) > 0) return getNumericValue(obj.hh_income)
  if (obj.income != null && getNumericValue(obj.income) > 0) return getNumericValue(obj.income)
  if (obj.median_income != null && getNumericValue(obj.median_income) > 0) return getNumericValue(obj.median_income)

  // 7. properties (GeoJSON)
  if (obj.properties) {
    const p = obj.properties
    if (p.hh_income != null && getNumericValue(p.hh_income) > 0) return getNumericValue(p.hh_income)
    if (p.median_hh_income != null && getNumericValue(p.median_hh_income) > 0) return getNumericValue(p.median_hh_income)
    if (p.median_income != null && getNumericValue(p.median_income) > 0) return getNumericValue(p.median_income)
    if (p.income != null && getNumericValue(p.income) > 0) return getNumericValue(p.income)
    if (p.MEDHHINC != null && getNumericValue(p.MEDHHINC) > 0) return getNumericValue(p.MEDHHINC)
  }

  // 8. Child collections
  if (Array.isArray(obj.geopolygons) && obj.geopolygons.length > 0) {
    const valid = obj.geopolygons.map(extractIncome).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }
  if (Array.isArray(obj.boundaries) && obj.boundaries.length > 0) {
    const valid = obj.boundaries.map(extractIncome).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }
  if (Array.isArray(obj.shapes) && obj.shapes.length > 0) {
    const valid = obj.shapes.map(extractIncome).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }

  return 0
}

const resolveHouseholdIncome = (item, rightSidebarStore, selectionStore) => {
  // 1. RightSidebarStore medianHouseholdIncome computed getter
  if (rightSidebarStore) {
    if (rightSidebarStore.medianHouseholdIncome !== undefined && rightSidebarStore.medianHouseholdIncome !== null) {
      const val = getNumericValue(rightSidebarStore.medianHouseholdIncome)
      if (val > 0) return val
    }
    if (rightSidebarStore.medianHHIncome != null) {
      const val = getNumericValue(rightSidebarStore.medianHHIncome)
      if (val > 0) return val
    }
    if (rightSidebarStore.averageIncome != null) {
      const val = getNumericValue(rightSidebarStore.averageIncome)
      if (val > 0) return val
    }
  }

  // 2. RightSidebarStore single polygon
  if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
    const val = extractIncome(rightSidebarStore.singlePolygonData)
    if (val > 0) return val
  }
  if (rightSidebarStore && Array.isArray(rightSidebarStore.rawSinglePolygonData) && rightSidebarStore.rawSinglePolygonData.length > 0) {
    const val = extractIncome(rightSidebarStore.rawSinglePolygonData[0])
    if (val > 0) return val
  }

  // 3. Item prop
  if (item) {
    const val = extractIncome(item)
    if (val > 0) return val
  }

  // 4. SelectionStore item
  if (selectionStore && selectionStore.item) {
    const val = extractIncome(selectionStore.item)
    if (val > 0) return val
  }

  // 5. Multi-selection items
  if (selectionStore && Array.isArray(selectionStore.selectedMenuItems) && selectionStore.selectedMenuItems.length > 0) {
    const valid = selectionStore.selectedMenuItems.map(extractIncome).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }

  // 6. Polygons in right sidebar
  const polyList = (rightSidebarStore && Array.isArray(rightSidebarStore.rawPolygonData) && rightSidebarStore.rawPolygonData.length > 0)
    ? rightSidebarStore.rawPolygonData
    : (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData) ? rightSidebarStore.polygonData : [])

  if (polyList.length > 0) {
    const valid = polyList.map(extractIncome).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }

  // 7. incomeAndPopDataForChart
  if (rightSidebarStore && Array.isArray(rightSidebarStore.incomeAndPopDataForChart) && rightSidebarStore.incomeAndPopDataForChart.length > 0) {
    const valid = rightSidebarStore.incomeAndPopDataForChart.map(d => getNumericValue(d.income)).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }

  // 8. incomeProgressData
  if (rightSidebarStore && Array.isArray(rightSidebarStore.incomeProgressData) && rightSidebarStore.incomeProgressData.length > 0) {
    const valid = rightSidebarStore.incomeProgressData.map(d => getNumericValue(d.income)).filter(v => v > 0)
    if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
  }

  // 9. populationData (API)
  if (rightSidebarStore && rightSidebarStore.populationData) {
    const popData = rightSidebarStore.populationData
    if (Array.isArray(popData.data) && popData.data.length > 0) {
      const y2021 = popData.data.find(d => (String(d.year) === '2021' || d.year === 2021) && (getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0))
      if (y2021) {
        const val = getNumericValue(y2021.average_income) || getNumericValue(y2021.total_income)
        if (val > 0) return val
      }
      const available = popData.data.filter(d => getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0)
      if (available.length > 0) {
        const latest = available[available.length - 1]
        const val = getNumericValue(latest.average_income) || getNumericValue(latest.total_income)
        if (val > 0) return val
      }
    }
    if (Array.isArray(popData.dissolved_data) && popData.dissolved_data.length > 0) {
      const y2021 = popData.dissolved_data.find(d => (String(d.year) === '2021' || d.year === 2021) && (getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0))
      if (y2021) {
        const val = getNumericValue(y2021.average_income) || getNumericValue(y2021.total_income)
        if (val > 0) return val
      }
      const available = popData.dissolved_data.filter(d => getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0)
      if (available.length > 0) {
        const latest = available[available.length - 1]
        const val = getNumericValue(latest.average_income) || getNumericValue(latest.total_income)
        if (val > 0) return val
      }
    }
  }

  return null
}

const formatNumberValue = (val) => {
  if (val == null || isNaN(Number(val))) return '0'
  return Math.round(Number(val)).toLocaleString('en-US')
}

const resolveItemName = (elem) => {
  if (!elem) return ''
  if (elem.name && typeof elem.name === 'string' && elem.name.trim() && elem.name !== 'Selected Area' && elem.name !== 'Selected Location') return elem.name.trim()
  if (elem.title && typeof elem.title === 'string' && elem.title.trim() && elem.title !== 'Selected Area' && elem.title !== 'Selected Location') return elem.title.trim()
  if (elem.shape_name && typeof elem.shape_name === 'string' && elem.shape_name.trim()) return elem.shape_name.trim()
  if (elem.displayName && typeof elem.displayName === 'string' && elem.displayName.trim()) return elem.displayName.trim()
  if (elem.zip_code) return String(elem.zip_code).trim()
  if (elem.zipcode) return String(elem.zipcode).trim()
  if (elem.zip) return String(elem.zip).trim()
  if (elem.code) return String(elem.code).trim()
  if (elem.postcode) return String(elem.postcode).trim()
  if (elem.postal_code) return String(elem.postal_code).trim()
  if (elem.properties) {
    const p = elem.properties
    if (p.name && typeof p.name === 'string' && p.name.trim()) return p.name.trim()
    if (p.title && typeof p.title === 'string' && p.title.trim()) return p.title.trim()
    if (p.NAME && typeof p.NAME === 'string' && p.NAME.trim()) return p.NAME.trim()
    if (p.zip_code) return String(p.zip_code).trim()
    if (p.zipcode) return String(p.zipcode).trim()
    if (p.ZIP) return String(p.ZIP).trim()
    if (p.ZIPCODE) return String(p.ZIPCODE).trim()
  }
  return ''
}

const resolveGeometryColor = (elem, selectionStore, dataStore) => {
  if (!elem) return '#0E7490'
  const isSelectionItem = elem === selectionStore?.item ||
    (selectionStore?.colorEditItem && (elem === selectionStore.colorEditItem || elem.targetGroupId === selectionStore.colorEditItem.pk))
  if (isSelectionItem && selectionStore?.selectedColor != null) {
    if (typeof selectionStore.selectedColor === 'string' && (selectionStore.selectedColor.startsWith('#') || selectionStore.selectedColor.startsWith('rgb') || selectionStore.selectedColor.startsWith('hsl'))) {
      return selectionStore.selectedColor
    }
    const mc = dataStore?.markerColors?.find(m => String(m.id) === String(selectionStore.selectedColor))
    if (mc && mc.active_color) return mc.active_color
  }
  if (elem.shape_status && dataStore?.shapeStatuses) {
    const status = dataStore.shapeStatuses.find(s => s.id === elem.shape_status)
    if (status && status.color) return status.color
  }
  const colorId = elem.color_id != null ? elem.color_id : (typeof elem.color === 'number' ? elem.color : null)
  if (colorId != null && dataStore?.markerColors) {
    const mc = dataStore.markerColors.find(m => String(m.id) === String(colorId))
    if (mc && mc.active_color) return mc.active_color
  }
  if (elem.getColor && typeof elem.getColor === 'object' && elem.getColor.active_color) {
    return elem.getColor.active_color
  }
  if (elem.color && typeof elem.color === 'object') {
    if (elem.color.active_color) return elem.color.active_color
    if (elem.color.color) return elem.color.color
  }
  if (typeof elem.color === 'string' && (elem.color.startsWith('#') || elem.color.startsWith('rgb') || elem.color.startsWith('hsl'))) {
    return elem.color
  }
  if (typeof elem.color_code === 'string' && elem.color_code.startsWith('#')) return elem.color_code
  if (typeof elem.hex === 'string' && elem.hex.startsWith('#')) return elem.hex
  if (typeof elem.getColor === 'string') return elem.getColor
  if (typeof elem.getColor === 'function') {
    try {
      const c = elem.getColor()
      if (typeof c === 'string') return c
      if (c && typeof c === 'object' && c.active_color) return c.active_color
    } catch (e) {}
  }
  return '#0E7490'
}

const resolveGeometryTransparency = (elem, selectionStore) => {
  const isSelectionItem = elem === selectionStore?.item ||
    (selectionStore?.colorEditItem && (elem === selectionStore.colorEditItem || elem.targetGroupId === selectionStore.colorEditItem.pk))
  if (isSelectionItem && selectionStore?.selectedTransparency != null) {
    return Math.max(0.1, Math.min(1, (Number(selectionStore.selectedTransparency) || 50) / 100))
  }
  const tr = elem?.transparency != null ? elem.transparency : (elem?.color_transparency != null ? elem.color_transparency : 50)
  return Math.max(0.1, Math.min(1, (Number(tr) || 50) / 100))
}

const isIsochroneItem = (elem, geo) => {
  if (!elem && !geo) return false
  const typeStr = String(elem?.type || elem?.shape?.type || elem?.layer || '').toLowerCase()
  if (
    typeStr === 'isochrone' ||
    typeStr === 'isochrone_shape' ||
    typeStr === 'bulk_isochrone'
  ) {
    return true
  }
  if (elem?.travelTime != null || elem?.buffer?.travelTime != null) {
    return true
  }
  if (elem?.modeOfTransportation != null || elem?.buffer?.modeOfTransportation != null) {
    return true
  }
  if (typeof elem?.name === 'string' && /isochrone/i.test(elem.name)) {
    return true
  }
  if (typeof elem?.shape?.name === 'string' && /isochrone/i.test(elem.shape.name)) {
    return true
  }
  const geoProps = geo?.properties || (geo?.features && geo.features[0]?.properties) || {}
  if (geoProps.isochrone || geoProps.contour != null || geoProps.metric != null) {
    return true
  }
  if (typeof geoProps.name === 'string' && /isochrone/i.test(geoProps.name)) {
    return true
  }
  return false
}

const parseBorderString = (str) => {
  if (!str || typeof str !== 'string') return null
  const trimmed = str.trim()
  if (!trimmed || trimmed === 'none' || trimmed === '0' || trimmed === 'hidden') {
    return { none: true }
  }
  if (/^#([0-9a-f]{3,8})$/i.test(trimmed) || /^(rgb|hsl)a?\(.+?\)$/i.test(trimmed)) {
    return { color: trimmed }
  }
  if (/^[a-z]+$/i.test(trimmed)) {
    return { color: trimmed }
  }
  const parts = trimmed.split(/\s+/)
  let width = null
  let style = null
  let color = null
  for (const p of parts) {
    if (/^\d+(\.\d+)?(px)?$/i.test(p)) {
      width = parseFloat(p)
    } else if (/^(solid|dashed|dotted|double)$/i.test(p)) {
      style = p.toLowerCase()
    } else if (/^#|^rgb|^hsl|[a-z]+/i.test(p)) {
      color = p
    }
  }
  return {
    color,
    width,
    dashArray: style === 'dashed' ? '7' : (style === 'dotted' ? '3, 3' : null)
  }
}

const resolveGeometryBorder = (elem, geo, baseColor = '#0E7490', dataStore = null) => {
  if (!elem && !geo) {
    return { hasBorder: false, borderColor: baseColor, borderWidth: 2.5, borderDashArray: null, borderOpacity: 1 }
  }

  const geoProps = geo?.properties || (geo?.features && geo.features[0]?.properties) || {}
  const shape = elem?.shape || {}

  const rawBorder = elem?.border ?? shape.border ?? geoProps.border
  const rawBorderColor = elem?.borderColor ?? elem?.border_color ?? shape.borderColor ?? shape.border_color ??
    elem?.strokeColor ?? elem?.stroke_color ?? shape.strokeColor ?? shape.stroke_color ??
    elem?.stroke ?? shape.stroke ?? geoProps.borderColor ?? geoProps.border_color ?? geoProps.strokeColor ?? geoProps.stroke
  const rawBorderWidth = elem?.borderWidth ?? elem?.border_width ?? shape.borderWidth ?? shape.border_width ??
    elem?.strokeWidth ?? elem?.stroke_width ?? shape.strokeWidth ?? shape.stroke_width ??
    geoProps.borderWidth ?? geoProps.border_width ?? geoProps['stroke-width']
  const rawDashArray = elem?.borderDashArray ?? elem?.border_dash_array ?? elem?.dashArray ?? elem?.strokeDasharray ??
    elem?.stroke_dasharray ?? shape.dashArray ?? shape.strokeDasharray ??
    geoProps.borderDashArray ?? geoProps.dashArray ?? geoProps['stroke-dasharray']
  const rawBorderOpacity = elem?.borderOpacity ?? elem?.border_opacity ?? elem?.strokeOpacity ??
    shape.borderOpacity ?? geoProps.borderOpacity ?? geoProps['stroke-opacity']

  let inactiveColor = null
  const colorId = elem?.color_id != null ? elem.color_id : (shape.color_id != null ? shape.color_id : null)
  if (colorId != null && dataStore?.markerColors) {
    const mc = dataStore.markerColors.find(m => String(m.id) === String(colorId))
    if (mc && mc.inactive_color) inactiveColor = mc.inactive_color
  }
  if (!inactiveColor && (elem?.inactive_color || shape.inactive_color)) {
    inactiveColor = elem?.inactive_color || shape.inactive_color
  }
  if (!inactiveColor && elem?.color && typeof elem.color === 'object' && elem.color.inactive_color) {
    inactiveColor = elem.color.inactive_color
  }

  let parsedBorder = null
  if (rawBorder != null) {
    if (typeof rawBorder === 'string') {
      parsedBorder = parseBorderString(rawBorder)
    } else if (typeof rawBorder === 'object') {
      parsedBorder = rawBorder
    } else if (rawBorder === true) {
      parsedBorder = { enabled: true }
    } else if (rawBorder === false) {
      parsedBorder = { none: true }
    }
  }

  if (parsedBorder && parsedBorder.none) {
    return { hasBorder: false, borderColor: baseColor, borderWidth: 0, borderDashArray: null, borderOpacity: 0 }
  }

  const isIso = isIsochroneItem(elem, geo)

  const hasExplicitBorder = Boolean(
    parsedBorder ||
    rawBorderColor ||
    rawBorderWidth != null ||
    rawDashArray != null ||
    elem?.hasBorder ||
    shape.hasBorder ||
    geoProps.hasBorder
  )

  if (hasExplicitBorder || isIso) {
    const finalBorderColor =
      parsedBorder?.color ||
      rawBorderColor ||
      (isIso ? (inactiveColor || '#ff5500') : (inactiveColor || '#ff5500' || baseColor))

    const finalBorderWidth = Number(
      parsedBorder?.width ??
      rawBorderWidth ??
      (isIso ? 3 : 3)
    )

    const finalDashArray =
      parsedBorder?.dashArray ||
      rawDashArray ||
      (isIso ? '7' : null)

    const finalOpacity = rawBorderOpacity != null ? Number(rawBorderOpacity) : 1

    return {
      hasBorder: true,
      borderColor: finalBorderColor || baseColor,
      borderWidth: !isNaN(finalBorderWidth) && finalBorderWidth >= 0 ? finalBorderWidth : 3,
      borderDashArray: finalDashArray ? String(finalDashArray) : null,
      borderOpacity: !isNaN(finalOpacity) ? finalOpacity : 1
    }
  }

  return {
    hasBorder: false,
    borderColor: baseColor,
    borderWidth: 2.5,
    borderDashArray: null,
    borderOpacity: 1
  }
}

const extractGeoJSON = (elem) => {
  if (!elem) return null
  let g = elem.geoJSON || elem.region || elem.geometry || elem.geometries || elem.polygon || elem.the_geom || elem.isochronePolygonGeoJson || (elem.shape && (elem.shape.geoJSON || elem.shape.region || elem.shape.polygon || elem.shape.the_geom))
  if (typeof g === 'string') {
    try {
      g = JSON.parse(g)
    } catch (e) {
      try {
        g = new Wkt.Wkt().read(g).toJson()
      } catch (err) {
        g = null
      }
    }
  }
  if (!g || typeof g !== 'object' || Array.isArray(g) || !g.type) g = null
  const name = resolveItemName(elem) || elem.name || elem.title || (g && g.properties && (g.properties.name || g.properties.title)) || (isIsochroneItem(elem, g) ? 'Isochrone' : '')
  if (g) {
    if (typeof g === 'object' && !Array.isArray(g)) {
      if (!g.properties) g.properties = {}
      if (name && !g.properties.name) g.properties.name = name
    }
    return g
  }
  const lat = elem.lat ?? elem.latitude ?? elem.position?.latLng?.lat ?? elem.position?.latitude ?? elem.location?.lat ?? elem.location?.latitude
  const lng = elem.lng ?? elem.longitude ?? elem.position?.latLng?.lng ?? elem.position?.longitude ?? elem.location?.lng ?? elem.location?.longitude
  if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [Number(lng), Number(lat)]
      },
      properties: {
        name,
        title: name,
        isMarker: true,
        icon_url: elem.icon_url || null
      }
    }
  }
  return null
}

const collectGeometries = (item, rightSidebarStore, selectionStore, dataStore) => {
  const list = []
  const seenIds = new Set()

  const addGeometry = (elem) => {
    if (!elem) return
    const geo = extractGeoJSON(elem)
    if (!geo) return
    const elemId = elem.id || elem.inner_id || elem.remote_db_id || elem.pk || (elem.shape && (elem.shape.id || elem.shape.inner_id)) || Math.random().toString()
    if (seenIds.has(elemId)) return
    seenIds.add(elemId)

    const elemName = resolveItemName(elem) || elem.name || elem.title || geo.properties?.name || geo.properties?.title || (isIsochroneItem(elem, geo) ? 'Isochrone' : '')
    const color = resolveGeometryColor(elem, selectionStore, dataStore)
    const transparency = resolveGeometryTransparency(elem, selectionStore)
    const border = resolveGeometryBorder(elem, geo, color, dataStore)
    list.push({
      id: elemId,
      name: elemName,
      label: elemName,
      geoJSON: geo,
      color,
      transparency,
      hasBorder: border.hasBorder,
      borderColor: border.borderColor,
      borderWidth: border.borderWidth,
      borderDashArray: border.borderDashArray,
      borderOpacity: border.borderOpacity,
      isPoint: geo.geometry?.type === 'Point' || geo.type === 'Point'
    })
  }

  const processItem = (obj) => {
    if (!obj) return
    const countBefore = list.length
    if (Array.isArray(obj.geopolygons)) obj.geopolygons.forEach(addGeometry)
    if (Array.isArray(obj.boundaries)) obj.boundaries.forEach(addGeometry)
    if (Array.isArray(obj.zipCodeBoundaries)) obj.zipCodeBoundaries.forEach(addGeometry)
    if (Array.isArray(obj.regions)) obj.regions.forEach(addGeometry)
    if (Array.isArray(obj.shapes)) obj.shapes.forEach(addGeometry)
    if (Array.isArray(obj.savedMarkers)) obj.savedMarkers.forEach(addGeometry)
    if (Array.isArray(obj.markers)) obj.markers.forEach(addGeometry)
    if (list.length === countBefore) addGeometry(obj)
  }

  const sources = []

  // 1. Multiple selected menu items
  if (selectionStore?.selectedMenuItems && selectionStore.selectedMenuItems.length > 0) {
    sources.push(() => selectionStore.selectedMenuItems.forEach(processItem))
  }

  // 2. Editable shape currently in creation or edit mode (e.g. Isochrone)
  if (selectionStore?.editableShape) {
    sources.push(() => processItem(selectionStore.editableShape))
  }

  // 3. SelectionStore item (individual shape or item selected)
  if (selectionStore?.item) {
    sources.push(() => processItem(selectionStore.item))
  }

  // 4. Prop item (passed from dropdown)
  if (item) {
    sources.push(() => processItem(item))
  }

  // 5. Single polygon in right sidebar. Try to find the matching model first
  if (rightSidebarStore?.singlePolygonData) {
    const raw = rightSidebarStore.singlePolygonData
    let model = null
    try {
      model = typeof rightSidebarStore.findTargetGroupShape === 'function'
        ? rightSidebarStore.findTargetGroupShape(raw)
        : null
    } catch (e) {}
    if (model) sources.push(() => processItem(model))
    sources.push(() => processItem(raw))
  }

  // 6. Target Group from RightSidebarStore
  if (rightSidebarStore?.targetGroupId && dataStore?.targetGroups) {
    const foundGroup = dataStore.targetGroups.find(
      g => g && String(g.pk) === String(rightSidebarStore.targetGroupId)
    )
    if (foundGroup) {
      sources.push(() => processItem(foundGroup))
    }
  }

  // 7. POI temporary markers
  if (dataStore?.poi && Array.isArray(dataStore.poi.temporaryMarkers) && dataStore.poi.temporaryMarkers.length > 0) {
    sources.push(() => dataStore.poi.temporaryMarkers.forEach(addGeometry))
  }

  // 8. Fallback: polygon data list from rightSidebarStore
  if (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData)) {
    sources.push(() => rightSidebarStore.polygonData.forEach(processItem))
  }

  for (const collect of sources) {
    collect()
    if (list.length > 0) break
  }

  return list
}

const calculateViewport = (geometries, uiStore) => {
  if (uiStore?.map) {
    try {
      const leafletElement = uiStore.map.leafletElement || (typeof uiStore.map.getCenter === 'function' ? uiStore.map : null)
      if (leafletElement && typeof leafletElement.getCenter === 'function') {
        const center = leafletElement.getCenter()
        return {
          center: [center.lat, center.lng],
          zoom: typeof leafletElement.getZoom === 'function' ? leafletElement.getZoom() : 12
        }
      }
    } catch (e) {}
  }

  let bounds = null
  geometries.forEach((g) => {
    if (g.geoJSON) {
      try {
        const b = geoJSONBounds(g.geoJSON)
        if (b) {
          if (!bounds) bounds = [b[0].slice(), b[1].slice()]
          else {
            bounds[0][0] = Math.min(bounds[0][0], b[0][0])
            bounds[0][1] = Math.min(bounds[0][1], b[0][1])
            bounds[1][0] = Math.max(bounds[1][0], b[1][0])
            bounds[1][1] = Math.max(bounds[1][1], b[1][1])
          }
        }
      } catch (e) {}
    }
  })

  if (bounds) {
    const centerLat = (bounds[0][0] + bounds[1][0]) / 2
    const centerLng = (bounds[0][1] + bounds[1][1]) / 2
    const latDiff = Math.abs(bounds[1][0] - bounds[0][0])
    const lngDiff = Math.abs(bounds[1][1] - bounds[0][1])
    const maxDiff = Math.max(latDiff, lngDiff)
    let calcZoom = 12
    if (maxDiff > 30) calcZoom = 3
    else if (maxDiff > 15) calcZoom = 4
    else if (maxDiff > 10) calcZoom = 5
    else if (maxDiff > 5) calcZoom = 6
    else if (maxDiff > 2) calcZoom = 8
    else if (maxDiff > 1) calcZoom = 9
    else if (maxDiff > 0.5) calcZoom = 10
    else if (maxDiff > 0.2) calcZoom = 11
    else if (maxDiff > 0.08) calcZoom = 12
    else calcZoom = 13

    return {
      center: [centerLat, centerLng],
      zoom: calcZoom
    }
  }

  return { center: [51.505, -0.09], zoom: 12 }
}

const buildWhatsAppMessage = (item, rightSidebarStore, selectionStore, sharedUrl = '') => {
  const shapeName = resolveShapeName(item, rightSidebarStore, selectionStore)
  const daytimePop = resolveDaytimePopulation(item, rightSidebarStore, selectionStore)
  const residentialPop = resolveResidentialPopulation(item, rightSidebarStore, selectionStore)
  const income = resolveHouseholdIncome(item, rightSidebarStore, selectionStore)

  const lines = [
    'Check out this new territory I found:',
    shapeName,
    `Daytime population: ${formatNumberValue(daytimePop)}`,
    `Residential population: ${formatNumberValue(residentialPop != null ? residentialPop : 0)}`
  ]

  const numericIncome = getNumericValue(income)
  if (numericIncome > 0) {
    lines.push(`Household income: $${formatNumberValue(numericIncome)}`)
  }

  lines.push(sharedUrl || 'www.populationexplorer.com')

  const result = lines.join('\n')
  return result
}

const generateFallbackCSV = (item, rightSidebarStore, name) => {
  const rows = []
  const title = name || (item && (item.name || item.title)) || (rightSidebarStore && rightSidebarStore.targetGroupName) || 'Export'

  rows.push([title])
  rows.push([])

  // 1. Population breakdown by year
  const popStats = item?.stats?.total_population || rightSidebarStore?.polygonTrendsData || []
  if (Array.isArray(popStats) && popStats.length > 0) {
    rows.push(['Year', 'Total Population'])
    popStats.forEach(p => {
      const year = p.year || p.name || ''
      const val = p.value != null ? Math.round(p.value) : (p.population != null ? Math.round(p.population) : '')
      if (year || val !== '') {
        rows.push([year, val])
      }
    })
    rows.push([])
  }

  // 2. Gender breakdown
  const maleStats = item?.stats?.male_population || []
  const femaleStats = item?.stats?.female_population || []
  if (maleStats.length > 0 || femaleStats.length > 0) {
    rows.push(['Year', 'Male Population', 'Female Population'])
    const years = Array.from(new Set([...maleStats.map(m => m.year), ...femaleStats.map(f => f.year)]))
    years.forEach(y => {
      const mVal = maleStats.find(m => m.year === y)?.value
      const fVal = femaleStats.find(f => f.year === y)?.value
      rows.push([y, mVal != null ? Math.round(mVal) : '', fVal != null ? Math.round(fVal) : ''])
    })
    rows.push([])
  }

  // 3. Income stats
  const incomeStats = item?.stats?.hh_income || []
  if (Array.isArray(incomeStats) && incomeStats.length > 0) {
    const validIncome = incomeStats.filter(inc => inc && (getNumericValue(inc.value) > 0 || getNumericValue(inc.income) > 0 || getNumericValue(inc.hh_income) > 0))
    if (validIncome.length > 0) {
      rows.push(['Year', 'Median Household Income'])
      validIncome.forEach(inc => {
        const val = getNumericValue(inc.value) || getNumericValue(inc.income) || getNumericValue(inc.hh_income)
        rows.push([inc.year || '', Math.round(val)])
      })
      rows.push([])
    }
  }

  // 4. Area & density
  const areaVal = item?.stats?.area_sqkm || item?.stats?.area_km || item?.area || item?.area_sqkm
  const densityVal = item?.stats?.density || item?.density
  if (areaVal != null || densityVal != null) {
    rows.push(['Metric', 'Value'])
    if (areaVal != null) rows.push(['Area (sq km)', Math.round(Number(areaVal) * 100) / 100])
    if (densityVal != null) rows.push(['Density (people/sq km)', Math.round(Number(densityVal) * 100) / 100])
    rows.push([])
  }

  // 5. If summary rows are still empty, build single summary
  if (rows.length <= 2) {
    const totalPop = (rightSidebarStore && rightSidebarStore.totalPopulation != null) ? rightSidebarStore.totalPopulation : (item && item.population)
    const rawIncome = (rightSidebarStore && rightSidebarStore.medianHouseholdIncome != null)
      ? rightSidebarStore.medianHouseholdIncome
      : ((rightSidebarStore && rightSidebarStore.medianHHIncome != null)
          ? rightSidebarStore.medianHHIncome
          : (item && item.income))
    const numIncome = getNumericValue(rawIncome)
    rows.push(['Metric', 'Value'])
    rows.push(['Total Population', totalPop != null && !isNaN(totalPop) ? Math.round(totalPop) : 0])
    if (numIncome > 0) {
      rows.push(['Median Household Income', Math.round(numIncome)])
    }
  }

  const escapeCell = (val) => {
    if (val === null || val === undefined) return ''
    const str = String(val)
    // Prevent Excel from auto-converting age brackets like 5-9 or 10-14 to dates (e.g. 9-May, 14-Oct)
    if (/^\d+-\d+(\+)?$/.test(str)) {
      return `="${str}"`
    }
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  return rows.map(r => r.map(escapeCell).join(',')).join('\n')
}

const ShareTriggerIcon = () => (
  <HugeiconsIcon
    icon={Share01Icon}
    size={16}
    color='currentColor'
    strokeWidth={1.8}
  />
)

const CopyIcon = () => (
  <HugeiconsIcon
    icon={CopyIconFromHuge}
    size={24}
    color='currentColor'
    strokeWidth={1.5}
  />
)

const DownloadIcon = () => (
  <HugeiconsIcon
    icon={Download01Icon}
    size={24}
    color='currentColor'
    strokeWidth={1.5}
  />
)

const EmailIcon = () => (
  <HugeiconsIcon
    icon={Mail01Icon}
    size={24}
    color='currentColor'
    strokeWidth={1.5}
  />
)

const WhatsAppIcon = () => (
  <HugeiconsIcon
    icon={WhatsappIconFromHuge}
    size={24}
    color='currentColor'
    strokeWidth={1.5}
  />
)

const ExportDocumentIcon = () => (
  <HugeiconsIcon
    icon={FileExportIcon}
    size={24}
    color='currentColor'
    strokeWidth={1.5}
  />
)

const sendWhatsAppMessage = (message) => {
  const encodedText = encodeURIComponent(message)
  const apiUrl = `https://api.whatsapp.com/send?text=${encodedText}`
  window.open(apiUrl, '_blank')
}

const LoadingSpinnerIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M21 12a9 9 0 1 1-6.219-8.56' />
  </svg>
)

const CheckCircleIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='8' cy='8' r='6.5' />
    <path d='M5.2 8.2l2 2 3.8-3.8' />
  </svg>
)

const CloseModalIcon = () => (
  <svg width='14' height='14' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M12 4L4 12M4 4l8 8' />
  </svg>
)

const FEEDBACK_OPTIONS = {
  copy: { id: 'copy', text: 'Image copied to clipboard', iconType: 'check' },
  download: { id: 'download', text: 'Image saved', iconType: 'check' },
  email: { id: 'email', text: 'Email sent', iconType: 'check' },
  export_csv: { id: 'export_csv', text: 'CSV downloaded', iconType: 'check' },
  export_kml: { id: 'export_kml', text: 'KML/KMZ downloaded', iconType: 'check' },
  whatsapp: { id: 'whatsapp', text: 'Sent to WhatsApp...', iconType: 'whatsapp' },
  generating_url: { id: 'generating_url', text: 'Generating shared link...', iconType: 'loading' }
}

const resolveBoundaryId = (item, selectionStore, dataStore) => {
  if (!item) return null
  if (item.inner_id != null) return item.inner_id

  if (selectionStore && selectionStore.item && selectionStore.item.inner_id != null) {
    return selectionStore.item.inner_id
  }

  if (dataStore && Array.isArray(dataStore.targetGroups)) {
    for (const group of dataStore.targetGroups) {
      if (Array.isArray(group.boundaries)) {
        const found = group.boundaries.find(b =>
          (item.id != null && (String(b.id) === String(item.id) || String(b.inner_id) === String(item.id) || String(b.remote_db_id) === String(item.id))) ||
          (item.remote_db_id != null && String(b.remote_db_id) === String(item.remote_db_id)) ||
          (item.name && b.name === item.name)
        )
        if (found && found.inner_id != null) return found.inner_id
      }
      if (Array.isArray(group.zipCodeBoundaries)) {
        const found = group.zipCodeBoundaries.find(b =>
          (item.id != null && (String(b.id) === String(item.id) || String(b.inner_id) === String(item.id) || String(b.remote_db_id) === String(item.id))) ||
          (item.remote_db_id != null && String(b.remote_db_id) === String(item.remote_db_id)) ||
          (item.name && b.name === item.name)
        )
        if (found && found.inner_id != null) return found.inner_id
      }
    }
  }

  return item.inner_id || item.id || item.remote_db_id
}

const isTargetGroupItem = (item, selectionStore, rightSidebarStore) => {
  if (selectionStore && selectionStore.type === selectionTypes.TARGET_GROUP) {
    return true
  }
  if (item) {
    if (item.pk != null && (item.geopolygons !== undefined || item.boundaries !== undefined || item.zipCodeBoundaries !== undefined || item.savedMarkers !== undefined || typeof item.loadKML === 'function')) {
      return true
    }
    if (typeof item.getTypeLabel === 'function' && item.getTypeLabel() === 'Folder') {
      return true
    }
  }
  if (rightSidebarStore && rightSidebarStore.targetGroupId && !rightSidebarStore.singlePolygonData && (!selectionStore || !selectionStore.item || selectionStore.type === selectionTypes.TARGET_GROUP)) {
    return true
  }
  return false
}

const ShareExportDropdown = ({
  selectionStore,
  dataStore,
  rightSidebarStore,
  uiStore,
  t,
  onAction = () => {}
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isKmlModalOpen, setIsKmlModalOpen] = useState(false)
  const [selectedFormat, setSelectedFormat] = useState('kmz')
  const [selectedShapesScope, setSelectedShapesScope] = useState('all_shapes')
  const [isExportingKml, setIsExportingKml] = useState(false)
  const [isShareImageModalOpen, setIsShareImageModalOpen] = useState(false)
  const [shareImageAction, setShareImageAction] = useState('copy')
  const [isShareByEmailModalOpen, setIsShareByEmailModalOpen] = useState(false)
  const [emailImageData, setEmailImageData] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [isFading, setIsFading] = useState(false)
  const dropdownRef = useRef(null)
  const feedbackTimerRef = useRef(null)
  const fadeTimerRef = useRef(null)

  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen)

  const showFeedback = (idOrData) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)

    const feedbackObj = typeof idOrData === 'string' ? { id: idOrData } : (idOrData || { id: 'copy' })
    setFeedback(feedbackObj)
    setIsFading(false)

    if (feedbackObj.isLoading) {
      return
    }

    fadeTimerRef.current = setTimeout(() => {
      setIsFading(true)
    }, 2200)

    feedbackTimerRef.current = setTimeout(() => {
      setFeedback(null)
      setIsFading(false)
    }, 2550)
  }

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    }
  }, [])

  const resolveTargetItem = () => {
    if (selectionStore && selectionStore.editableShape) {
      return selectionStore.editableShape
    }

    if (selectionStore && selectionStore.item) {
      return selectionStore.item
    }

    if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
      const raw = rightSidebarStore.singlePolygonData
      let model = null
      try {
        model = typeof rightSidebarStore.findTargetGroupShape === 'function'
          ? rightSidebarStore.findTargetGroupShape(raw)
          : null
      } catch (e) {}
      if (model) return model
      return raw
    }

    if (rightSidebarStore && rightSidebarStore.targetGroupId && dataStore && dataStore.targetGroups) {
      const foundGroup = dataStore.targetGroups.find(
        g => g && String(g.pk) === String(rightSidebarStore.targetGroupId)
      )
      if (foundGroup) return foundGroup
    }

    return null
  }

  const handleExportCsv = async () => {
    if (uiStore && uiStore.user && (uiStore.user.type === 'starter' || uiStore.user.isStarter)) {
      startProCheckout({ uiStore, t })
      return
    }

    try {
      const item = resolveTargetItem()
      const name = (item && (item.name || item.title)) || (rightSidebarStore && rightSidebarStore.targetGroupName) || 'export'

      // 1. If item has an analysis object with exportCSV
      if (item && item.analysis && typeof item.analysis.exportCSV === 'function') {
        await item.analysis.exportCSV(name, item)
        showFeedback('export_csv')
        return
      }

      // 2. If item has its own exportCSV method
      if (item && typeof item.exportCSV === 'function') {
        await item.exportCSV()
        showFeedback('export_csv')
        return
      }

      // 3. If item corresponds to a model in targetGroups
      if (item && item.id != null && dataStore && dataStore.targetGroups) {
        let matchedExport = null
        for (const tg of dataStore.targetGroups) {
          if (String(tg.pk) === String(item.id) || String(tg.id) === String(item.id)) {
            if (tg.analysis && typeof tg.analysis.exportCSV === 'function') {
              matchedExport = () => tg.analysis.exportCSV(name, tg)
              break
            }
          }
          if (Array.isArray(tg.geopolygons)) {
            const foundGeo = tg.geopolygons.find(p => String(p.id) === String(item.id) || String(p.inner_id) === String(item.id))
            if (foundGeo && foundGeo.analysis && typeof foundGeo.analysis.exportCSV === 'function') {
              matchedExport = () => foundGeo.analysis.exportCSV(name, foundGeo)
              break
            }
          }
          if (Array.isArray(tg.boundaries)) {
            const foundB = tg.boundaries.find(b => String(b.id) === String(item.id) || String(b.remote_db_id) === String(item.id) || String(b.inner_id) === String(item.id))
            if (foundB && foundB.analysis && typeof foundB.analysis.exportCSV === 'function') {
              matchedExport = () => foundB.analysis.exportCSV(name, foundB)
              break
            }
          }
        }
        if (matchedExport) {
          await matchedExport()
          showFeedback('export_csv')
          return
        }
      }

      // 4. If selectionStore.item is set and has exportCSV
      if (selectionStore && selectionStore.item) {
        if (selectionStore.item.analysis && typeof selectionStore.item.analysis.exportCSV === 'function') {
          await selectionStore.item.analysis.exportCSV(name, selectionStore.item)
          showFeedback('export_csv')
          return
        }
        if (typeof selectionStore.item.exportCSV === 'function') {
          await selectionStore.item.exportCSV()
          showFeedback('export_csv')
          return
        }
      }

      // 5. If target group is active in rightSidebarStore
      if (rightSidebarStore && rightSidebarStore.targetGroupId && dataStore && dataStore.targetGroups) {
        const foundGroup = dataStore.targetGroups.find(
          g => g && String(g.pk) === String(rightSidebarStore.targetGroupId)
        )
        if (foundGroup && foundGroup.analysis && typeof foundGroup.analysis.exportCSV === 'function') {
          await foundGroup.analysis.exportCSV(name, foundGroup)
          showFeedback('export_csv')
          return
        }
      }

      // 6. Direct CSV generation fallback from raw stats / rightSidebarStore
      const csvContent = generateFallbackCSV(item, rightSidebarStore, name)
      downloadCSV(name, csvContent)
      showFeedback('export_csv')
    } catch (err) {
      console.error('Error exporting CSV:', err)
      if (uiStore && uiStore.addNotification) {
        uiStore.addNotification({
          text: 'Failed to export CSV. Please try again.',
          isError: true,
          category: 'Error'
        })
      }
    }
  }

  const handleExportKmlKmz = async (format, shapesScope) => {
    const isKml = (format || selectedFormat || 'kmz') === 'kml'
    const isKmz = !isKml
    const allShapes = (shapesScope || selectedShapesScope) === 'all_shapes'

    if (uiStore && uiStore.user && (uiStore.user.type === 'starter' || uiStore.user.isStarter)) {
      startProCheckout({ uiStore, t })
      return
    }

    setIsExportingKml(true)
    const item = resolveTargetItem()
    const config = { responseType: 'blob' }

    try {
      const name = (item && (item.name || item.title)) || (rightSidebarStore && rightSidebarStore.targetGroupName) || 'export'
      const isTG = isTargetGroupItem(item, selectionStore, rightSidebarStore)

      if (isTG) {
        // Target Group / Folder KML export
        uiStore.kmlFormat = isKml
        uiStore.kmlAllShapes = allShapes

        if (item && typeof item.exportKml === 'function') {
          await item.exportKml(isKml, allShapes)
        } else if (item && item.pk != null) {
          await api.getTargetGroupKML(item.pk, { kmz_file: isKmz, all_shapes: allShapes })
          if (typeof item.loadKML === 'function') {
            await item.loadKML()
          } else if (dataStore && Array.isArray(dataStore.targetGroups)) {
            const foundGroup = dataStore.targetGroups.find(g => String(g.pk) === String(item.pk))
            if (foundGroup && typeof foundGroup.loadKML === 'function') {
              await foundGroup.loadKML()
            }
          }
        } else if (rightSidebarStore && rightSidebarStore.targetGroupId) {
          const groupPk = rightSidebarStore.targetGroupId
          const foundGroup = dataStore && dataStore.targetGroups && dataStore.targetGroups.find(g => String(g.pk) === String(groupPk))
          if (foundGroup && typeof foundGroup.exportKml === 'function') {
            await foundGroup.exportKml(isKml, allShapes)
          } else {
            await api.getTargetGroupKML(groupPk, { kmz_file: isKmz, all_shapes: allShapes })
            if (foundGroup && typeof foundGroup.loadKML === 'function') {
              await foundGroup.loadKML()
            }
          }
        } else if (uiStore && typeof uiStore.exportKml === 'function') {
          await uiStore.exportKml()
        }
      } else if (item) {
        // Boundary, Zip Code Boundary, Geopolygon, or Marker
        const isBoundaryOrZip = Boolean(
          item.isBoundary ||
          item.isZipCode ||
          item.is_zip_code ||
          item.is_boundary ||
          item.inner_id != null ||
          (selectionStore && (selectionStore.type === selectionTypes.BOUNDARY || selectionStore.type === selectionTypes.ZIP_CODE_BOUNDARY))
        )

        if (isBoundaryOrZip) {
          // Admin Boundaries & Zip Code Boundaries
          if (typeof item.exportKml === 'function') {
            await item.exportKml(isKml)
          } else {
            const boundaryId = resolveBoundaryId(item, selectionStore, dataStore)
            const response = await api.getBoundaryKML(boundaryId, isKmz, config)
            downloadKML(name, response.data, isKml)
          }
        } else if (item.type === 'marker' || item.place_id || (selectionStore && selectionStore.type === selectionTypes.SAVED_MARKER)) {
          // Saved Marker
          if (typeof item.exportKml === 'function') {
            await item.exportKml(isKml)
          } else {
            const response = await api.getMarkerKML(item.id, isKmz, config)
            downloadKML(name, response.data, isKml)
          }
        } else {
          // Geopolygon / Shape
          if (typeof item.exportKml === 'function') {
            await item.exportKml(isKml)
          } else if (item.id != null) {
            const response = await api.getRegionKML(item.id, isKmz, config)
            downloadKML(name, response.data, isKml)
          } else if (uiStore && typeof uiStore.exportKml === 'function') {
            uiStore.kmlFormat = isKml
            await uiStore.exportKml()
          }
        }
      } else if (uiStore && typeof uiStore.exportKml === 'function') {
        uiStore.kmlFormat = isKml
        uiStore.kmlAllShapes = allShapes
        await uiStore.exportKml()
      }

      setIsKmlModalOpen(false)
      showFeedback('export_kml')
    } catch (err) {
      console.error('KML/KMZ Export error:', err)
      if (uiStore && uiStore.addNotification) {
        uiStore.addNotification({
          text: 'Failed to export KML/KMZ',
          isError: true,
          category: 'Error'
        })
      }
    } finally {
      setIsExportingKml(false)
    }
  }

  const handleProceedToEmail = (base64Data) => {
    setEmailImageData(base64Data)
    setIsShareImageModalOpen(false)
    setIsShareByEmailModalOpen(true)
  }

  const handleItemClick = async (actionName) => {
    onAction(actionName)
    setIsOpen(false)

    try {
      switch (actionName) {
        case 'copy': {
          setShareImageAction('copy')
          setIsShareImageModalOpen(true)
          break
        }
        case 'download': {
          setShareImageAction('download')
          setIsShareImageModalOpen(true)
          break
        }
        case 'email': {
          setShareImageAction('email')
          setEmailImageData(null)
          setIsShareImageModalOpen(true)
          break
        }
        case 'whatsapp': {
          showFeedback({ id: 'generating_url', text: 'Generating shared link...', isLoading: true })
          const targetItem = resolveTargetItem()
          const name = resolveShapeName(targetItem, rightSidebarStore, selectionStore)
          const daytimePop = resolveDaytimePopulation(targetItem, rightSidebarStore, selectionStore)
          const residentialPop = resolveResidentialPopulation(targetItem, rightSidebarStore, selectionStore)
          const income = resolveHouseholdIncome(targetItem, rightSidebarStore, selectionStore)

          let sharedUrl = ''
          try {
            const geoms = collectGeometries(targetItem, rightSidebarStore, selectionStore, dataStore)
            const vp = calculateViewport(geoms, uiStore) || { center: [37.7749, -122.4194], zoom: 12 }
            const geometriesPayload = (geoms || []).map((g) => ({
              id: g.id,
              geoJSON: g.geoJSON,
              color: g.color,
              transparency: g.transparency,
              hasBorder: g.hasBorder,
              borderColor: g.borderColor,
              borderWidth: g.borderWidth,
              borderDashArray: g.borderDashArray,
              borderOpacity: g.borderOpacity,
              isPoint: g.isPoint
            }))

            const res = await api.shareLocation({
              location_url: window.location.href,
              origin: window.location.origin,
              image: '',
              geometries: geometriesPayload,
              map_state: {
                center: vp?.center || [37.7749, -122.4194],
                zoom: vp?.zoom || 12,
                density_layer: uiStore?.densityLayer || 'none',
                current_year: uiStore?.currentYear || 2016
              },
              target_name: name,
              daytime_population: daytimePop || 0,
              residential_population: residentialPop || 0,
              household_income: income || 0,
              description: (targetItem?.description || targetItem?.properties?.description || rightSidebarStore?.singlePolygonData?.description || '')
            })
            if (res && res.data && res.data.shared_url) {
              sharedUrl = res.data.shared_url
            }
          } catch (shareErr) {
            console.warn('[ShareExportDropdown] Failed to generate share URL for WhatsApp:', shareErr)
          }

          const whatsappMsg = buildWhatsAppMessage(targetItem, rightSidebarStore, selectionStore, sharedUrl)
          sendWhatsAppMessage(whatsappMsg)
          showFeedback('whatsapp')
          break
        }
        case 'export_csv': {
          await handleExportCsv()
          break
        }
        case 'export_kml': {
          setSelectedFormat('kmz')
          setSelectedShapesScope(uiStore?.kmlAllShapes === false ? 'outer_boundary' : 'all_shapes')
          setIsKmlModalOpen(true)
          break
        }
        default:
          break
      }
    } catch (err) {
      console.error('Action error', actionName, err)
    }
  }

  const targetItem = resolveTargetItem()

  const hasData = Boolean(
    (!rightSidebarStore || (!rightSidebarStore.noData && rightSidebarStore.error !== 'No polygon data available')) &&
    (
      (targetItem && (
        targetItem.geoJSON ||
        (targetItem.geopolygons && targetItem.geopolygons.length > 0) ||
        (targetItem.boundaries && targetItem.boundaries.length > 0) ||
        (targetItem.zipCodeBoundaries && targetItem.zipCodeBoundaries.length > 0) ||
        (targetItem.regions && targetItem.regions.length > 0) ||
        (targetItem.stats && (
          (targetItem.stats.total_population && targetItem.stats.total_population.length > 0) ||
          (targetItem.stats.hh_income && targetItem.stats.hh_income.length > 0)
        )) ||
        (targetItem.analysis && targetItem.analysis.sum > 0)
      )) ||
      (selectionStore && selectionStore.selectedMenuItems && selectionStore.selectedMenuItems.length > 0) ||
      (selectionStore && selectionStore.item) ||
      (rightSidebarStore && (
        (rightSidebarStore.totalPopulation != null && rightSidebarStore.totalPopulation > 0) ||
        (rightSidebarStore.populationTrendsData && rightSidebarStore.populationTrendsData.length > 0) ||
        (rightSidebarStore.polygonData && rightSidebarStore.polygonData.length > 0)
      ))
    )
  )

  if (!hasData && !isShareByEmailModalOpen && !isShareImageModalOpen && !isKmlModalOpen && !feedback) {
    return null
  }

  const isTG = isTargetGroupItem(targetItem, selectionStore, rightSidebarStore)

  return (
    <>
      <div ref={dropdownRef} className='share-export-dropdown'>
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
          className={`share-export-dropdown__trigger ${isOpen ? 'share-export-dropdown__trigger--active' : ''}`}
          title='Share & Export'
        >
          <ShareTriggerIcon />
        </button>

        {isOpen && (
          <div className='share-export-dropdown__menu'>
            <button
              type='button'
              className='share-export-dropdown__item'
              onClick={() => handleItemClick('copy')}
            >
              <span className='share-export-dropdown__item-icon'><CopyIcon /></span>
              <span className='share-export-dropdown__item-text'>Copy</span>
            </button>

            <button
              type='button'
              className='share-export-dropdown__item'
              onClick={() => handleItemClick('download')}
            >
              <span className='share-export-dropdown__item-icon'><DownloadIcon /></span>
              <span className='share-export-dropdown__item-text'>Download</span>
            </button>

            <button
              type='button'
              className='share-export-dropdown__item'
              onClick={() => handleItemClick('email')}
            >
              <span className='share-export-dropdown__item-icon'><EmailIcon /></span>
              <span className='share-export-dropdown__item-text'>Email</span>
            </button>

            <button
              type='button'
              className='share-export-dropdown__item'
              onClick={() => handleItemClick('whatsapp')}
            >
              <span className='share-export-dropdown__item-icon'><WhatsAppIcon /></span>
              <span className='share-export-dropdown__item-text'>WhatsApp</span>
            </button>

            <div className='share-export-dropdown__divider' />

            <button
              type='button'
              className='share-export-dropdown__item'
              onClick={() => handleItemClick('export_csv')}
            >
              <span className='share-export-dropdown__item-icon'><ExportDocumentIcon /></span>
              <span className='share-export-dropdown__item-text'>Export CSV</span>
            </button>

            <button
              type='button'
              className='share-export-dropdown__item'
              onClick={() => handleItemClick('export_kml')}
            >
              <span className='share-export-dropdown__item-icon'><ExportDocumentIcon /></span>
              <span className='share-export-dropdown__item-text'>Export KML/KMZ</span>
            </button>
          </div>
        )}
      </div>

      {feedback && ReactDOM.createPortal(
        <div className='action-feedback-toast-container'>
          <div
            className={`action-feedback-toast ${isFading ? 'action-feedback-toast--fading' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`action-feedback-toast__icon-badge ${
                feedback.id === 'whatsapp'
                  ? 'action-feedback-toast__icon-badge--whatsapp'
                  : (feedback.isLoading || feedback.id === 'generating_url'
                    ? 'action-feedback-toast__icon-badge--loading'
                    : '')
              }`}
            >
              {feedback.id === 'whatsapp'
                ? <WhatsAppIcon />
                : (feedback.isLoading || feedback.id === 'generating_url'
                    ? <LoadingSpinnerIcon />
                    : <CheckCircleIcon />)}
            </div>
            <span className='action-feedback-toast__text'>
              {FEEDBACK_OPTIONS[feedback.id]?.text || feedback.text || 'Action completed'}
            </span>
            <button
              type='button'
              className='action-feedback-toast__close-btn'
              onClick={() => {
                if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
                if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
                setFeedback(null)
                setIsFading(false)
              }}
              aria-label='Close feedback'
            >
              <CloseModalIcon />
            </button>
          </div>
        </div>,
        document.body
      )}

      {isKmlModalOpen && ReactDOM.createPortal(
        <div
          className='kml-export-modal-overlay'
          onClick={() => {
            if (!isExportingKml) setIsKmlModalOpen(false)
          }}
        >
          <div
            className='kml-export-modal'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='kml-export-modal__header'>
              <h3 className='kml-export-modal__title'>Export KML/KMZ</h3>
              <button
                type='button'
                className='kml-export-modal__close-btn'
                onClick={() => {
                  if (!isExportingKml) setIsKmlModalOpen(false)
                }}
                aria-label='Close modal'
                disabled={isExportingKml}
              >
                <CloseModalIcon />
              </button>
            </div>

            <p className='kml-export-modal__description'>
              {isTG
                ? 'Choose shapes to export and file format.'
                : 'Choose a file format to export.'}
            </p>

            {isTG && (
              <div className='kml-export-modal__section'>
                <div className='kml-export-modal__section-title'>Shapes to export</div>
                <div className='kml-export-modal__options'>
                  <div
                    className={`kml-export-modal__option ${selectedShapesScope === 'all_shapes' ? 'kml-export-modal__option--selected' : ''}`}
                    onClick={() => setSelectedShapesScope('all_shapes')}
                  >
                    <div className={`kml-export-modal__radio ${selectedShapesScope === 'all_shapes' ? 'kml-export-modal__radio--selected' : ''}`}>
                      {selectedShapesScope === 'all_shapes' && <div className='kml-export-modal__radio-dot' />}
                    </div>
                    <span className='kml-export-modal__option-label'>Export all shapes</span>
                  </div>

                  <div
                    className={`kml-export-modal__option ${selectedShapesScope === 'outer_boundary' ? 'kml-export-modal__option--selected' : ''}`}
                    onClick={() => setSelectedShapesScope('outer_boundary')}
                  >
                    <div className={`kml-export-modal__radio ${selectedShapesScope === 'outer_boundary' ? 'kml-export-modal__radio--selected' : ''}`}>
                      {selectedShapesScope === 'outer_boundary' && <div className='kml-export-modal__radio-dot' />}
                    </div>
                    <span className='kml-export-modal__option-label'>Export outer boundary</span>
                  </div>
                </div>
              </div>
            )}

            <div className='kml-export-modal__section'>
              {isTG && (
                <div className='kml-export-modal__section-title'>File format</div>
              )}
              <div className='kml-export-modal__options'>
                <div
                  className={`kml-export-modal__option ${selectedFormat === 'kml' ? 'kml-export-modal__option--selected' : ''}`}
                  onClick={() => setSelectedFormat('kml')}
                >
                  <div className={`kml-export-modal__radio ${selectedFormat === 'kml' ? 'kml-export-modal__radio--selected' : ''}`}>
                    {selectedFormat === 'kml' && <div className='kml-export-modal__radio-dot' />}
                  </div>
                  <span className='kml-export-modal__option-label'>KML</span>
                </div>

                <div
                  className={`kml-export-modal__option ${selectedFormat === 'kmz' ? 'kml-export-modal__option--selected' : ''}`}
                  onClick={() => setSelectedFormat('kmz')}
                >
                  <div className={`kml-export-modal__radio ${selectedFormat === 'kmz' ? 'kml-export-modal__radio--selected' : ''}`}>
                    {selectedFormat === 'kmz' && <div className='kml-export-modal__radio-dot' />}
                  </div>
                  <span className='kml-export-modal__option-label'>KMZ</span>
                </div>
              </div>
            </div>

            <div className='kml-export-modal__footer'>
              <button
                type='button'
                className='kml-export-modal__cancel-btn'
                onClick={() => setIsKmlModalOpen(false)}
                disabled={isExportingKml}
              >
                Cancel
              </button>
              <button
                type='button'
                className='kml-export-modal__export-btn'
                disabled={!selectedFormat || isExportingKml}
                onClick={() => handleExportKmlKmz(selectedFormat, selectedShapesScope)}
              >
                {isExportingKml ? 'Exporting...' : 'Export'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ShareImageModal
        isOpen={isShareImageModalOpen}
        onClose={() => setIsShareImageModalOpen(false)}
        initialAction={shareImageAction}
        item={resolveTargetItem()}
        onProceedToEmail={handleProceedToEmail}
        onSuccess={(feedbackData) => showFeedback(feedbackData.id || (feedbackData.text?.includes('clipboard') ? 'copy' : 'download'))}
      />

      <ShareByEmailModal
        isOpen={isShareByEmailModalOpen}
        onClose={() => {
          setIsShareByEmailModalOpen(false)
          setEmailImageData(null)
        }}
        item={resolveTargetItem()}
        preGeneratedImage={emailImageData}
        onSuccess={() => showFeedback('email')}
      />
    </>
  )
}

export default withTranslation()(
  inject('selectionStore', 'dataStore', 'rightSidebarStore', 'uiStore')(
    observer(ShareExportDropdown)
  )
)
